import { LimitThreshold } from './incomeLimit.js';
import { IncomeLimitAreaFilter } from './incomeLimitArea.js';
import Partner from './partner.js';
import { DropdownItem } from './ui/dropdown.js';
import { TextInputType } from './ui/text.js';

// Canonical eligibility statuses used across API, search, and UI.
// These are persisted as exact strings in application versions and
// used for exact-match filtering in Elasticsearch and Postgres.
export enum EligibilityStatus {
  Eligible = 'Eligible',
  Ineligible = 'Ineligible',
  NotEvaluated = 'NotEvaluated',
  Undetermined = 'Undetermined',
  FailedToEvaluate = 'FailedToEvaluate',
}

export type EligibilityMessage = {
  eligible: string;
  ineligible: string;
};

export interface EligibilityConfig {
  id: string;
  partnerId: string;
  partner: Partner;
  description: string;
  title?: string;
  copy?: string;
  createdAt: Date;
  updatedAt: Date;
  deactivatedAt?: Date;
  questions: EligibilityQuestion[];
  message?: EligibilityMessage;
}

export enum EligibilityQuestionType {
  AMIComponent = 'AMIComponent',
  Content = 'Content',
  Dropdown = 'Dropdown',
  TextInput = 'TextInput',
  YesOrNo = 'YesOrNo',
}

interface EligibilityQuestionBase {
  id: string;
  description: string;
  key: string;
  copy?: string;
  createdAt: Date;
  updatedAt: Date;
  deactivatedAt?: Date;
}

interface AMIComponentProps {
  label: string;
  limitFilter: IncomeLimitAreaFilter;
  limitThreshold?: LimitThreshold;
}
export interface AMIComponent extends EligibilityQuestionBase {
  type: EligibilityQuestionType.AMIComponent;
  props: AMIComponentProps;
}

interface ContentProps {
  content: string;
  icon?: string;
}
interface Content extends EligibilityQuestionBase {
  type: EligibilityQuestionType.Content;
  props: ContentProps;
}

interface DropdownProps {
  answer: string[];
  label: string;
  items: DropdownItem[];
}
interface Dropdown extends EligibilityQuestionBase {
  type: EligibilityQuestionType.Dropdown;
  props: DropdownProps;
}

interface TextInputProps {
  answer: string;
  label: string;
  format?: TextInputType;
}
interface TextInput extends EligibilityQuestionBase {
  type: EligibilityQuestionType.TextInput;
  props: TextInputProps;
}

interface YesOrNoProps {
  answer: 'true' | 'false';
}
interface YesOrNo extends EligibilityQuestionBase {
  type: EligibilityQuestionType.YesOrNo;
  props: YesOrNoProps;
}

export type EligibilityQuestionProps =
  | AMIComponentProps
  | ContentProps
  | DropdownProps
  | TextInputProps
  | YesOrNoProps;

export type EligibilityQuestion = AMIComponent | Content | Dropdown | TextInput | YesOrNo;

// This type should only be used for the entity class implementation, since a class can only
// implement an object type with statically known members (TS2422). Logical implementations
// should use the EligibilityQuestion type above to take advantage of the compiled union type.
export type EligibilityQuestionEntity = Omit<EligibilityQuestion, 'props' | 'type'> & {
  props: EligibilityQuestionProps;
  type: EligibilityQuestionType;
};

export interface EligiblityPage {
  questions: EligibilityQuestion[];
}
